import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaArrowRight, FaChevronDown, FaDatabase, FaSearch, FaCut, FaNewspaper } from 'react-icons/fa';
import { theme } from '../../styles/theme';

// Animation variants - partially restored for Framer Motion v10.16.4 compatibility
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.2,
      duration: 0.6
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.6
    }
  }
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: theme.shadows.buttonHover,
    transition: { duration: 0.3 }
  },
  tap: { scale: 0.98, transition: { duration: 0.1 } }
};

// Hero Section
const HeroContainer = styled.section`
  background: linear-gradient(to right, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.4)), 
              url('/images/hero-bg.jpg') no-repeat center center;
  background-size: cover;
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  margin-top: -80px;
  padding: 0 15px;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 100%;
    background: ${theme.colors.gradient.glow};
    opacity: 0.7;
    pointer-events: none;
    z-index: 1;
  }
`;

const HeroContent = styled(motion.div)`
  max-width: 1200px;
  width: 100%;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  z-index: 2;
`;

const HeroH1 = styled(motion.h1)`
  color: ${theme.colors.secondary};
  font-size: ${theme.typography.fontSize['4xl']};
  margin-bottom: 24px;
  font-weight: ${theme.typography.fontWeight.bold};
  line-height: ${theme.typography.lineHeight.tight};
  letter-spacing: ${theme.typography.letterSpacing.tight};
  text-shadow: ${theme.shadows.text};
  max-width: 800px;
  
  @media screen and (max-width: 768px) {
    font-size: ${theme.typography.fontSize['3xl']};
  }
  
  @media screen and (max-width: 480px) {
    font-size: ${theme.typography.fontSize['2xl']};
  }
`;

const HeroP = styled(motion.p)`
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.xl};
  max-width: 600px;
  margin-bottom: 35px;
  line-height: ${theme.typography.lineHeight.relaxed};
  font-weight: ${theme.typography.fontWeight.medium};
  text-shadow: ${theme.shadows.text};
  
  @media screen and (max-width: 768px) {
    font-size: ${theme.typography.fontSize.lg};
  }
  
  @media screen and (max-width: 480px) {
    font-size: ${theme.typography.fontSize.md};
  }
`;

const HeroBtnWrapper = styled(motion.div)`
  display: flex;
  flex-direction: row;
  gap: 20px;
  
  @media screen and (max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }
`;

const MotionLink = motion(Link);
const HeroBtn = styled(MotionLink)`
  border-radius: ${theme.borderRadius.pill};
  background: ${({ primary }) => (primary ? theme.colors.gradient.gold : 'transparent')};
  white-space: nowrap;
  padding: ${({ big }) => (big ? '16px 48px' : '14px 32px')};
  color: ${({ dark }) => (dark ? theme.colors.primary : theme.colors.text.primary)};
  font-size: ${({ fontBig }) => (fontBig ? theme.typography.fontSize.lg : theme.typography.fontSize.base)};
  font-weight: ${theme.typography.fontWeight.semiBold};
  outline: none;
  border: ${({ primary }) => (primary ? 'none' : `2px solid ${theme.colors.border.light}`)};
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all ${theme.transitions.medium};
  text-decoration: none;
  box-shadow: ${({ primary }) => (primary ? theme.shadows.button : 'none')};
  position: relative;
  overflow: hidden;
  z-index: 1;
  
  @media screen and (max-width: 480px) {
    width: 100%;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ primary }) => (primary ? 'rgba(255, 255, 255, 0.1)' : 'rgba(212, 175, 55, 0.1)')};
    z-index: -1;
    transform: scaleX(0);
    transform-origin: right;
    transition: transform ${theme.transitions.medium};
  }
  
  &:hover::before {
    transform: scaleX(1);
    transform-origin: left;
  }
  
  &:hover {
    background: ${({ primary }) => (primary ? theme.colors.gradient.button : 'transparent')};
    color: ${({ primary }) => (primary ? theme.colors.text.secondary : theme.colors.text.accent)};
    border: ${({ primary }) => (primary ? 'none' : `2px solid ${theme.colors.border.gold}`)};
    box-shadow: ${({ primary }) => (primary ? theme.shadows.buttonHover : theme.shadows.gold)};
  }
  
  &:focus {
    outline: none;
    box-shadow: ${theme.shadows.focus};
  }
`;

// About Preview Section
const AboutPreviewContainer = styled.section`
  padding: 120px 0;
  background: ${theme.colors.gradient.dark};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${theme.colors.gradient.blueGlow};
    opacity: 0.4;
    pointer-events: none;
  }
`;

const AboutPreviewWrapper = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const AboutPreviewH2 = styled(motion.h2)`
  font-size: ${theme.typography.fontSize['3xl']};
  color: ${theme.colors.secondary};
  margin-bottom: 64px;
  font-weight: ${theme.typography.fontWeight.bold};
  letter-spacing: ${theme.typography.letterSpacing.wide};
  text-shadow: ${theme.shadows.text};
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -16px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: ${theme.colors.gradient.gold};
    border-radius: ${theme.borderRadius.pill};
  }
  
  @media screen and (max-width: 480px) {
    font-size: ${theme.typography.fontSize['2xl']};
  }
`;

const AboutPreviewP = styled(motion.p)`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.primary};
  max-width: 800px;
  margin: 0 auto 48px auto;
  line-height: ${theme.typography.lineHeight.relaxed};
  font-weight: ${theme.typography.fontWeight.regular};
  
  @media screen and (max-width: 480px) {
    font-size: ${theme.typography.fontSize.base};
  }
`;

const AboutPreviewBtn = styled(MotionLink)`
  display: inline-flex;
  align-items: center;
  background: transparent;
  color: ${theme.colors.secondary};
  font-size: ${theme.typography.fontSize.lg};
  text-decoration: none;
  font-weight: ${theme.typography.fontWeight.semiBold};
  padding: 8px 16px;
  border-radius: ${theme.borderRadius.medium};
  transition: all ${theme.transitions.medium};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: ${theme.colors.gradient.gold};
    transition: width ${theme.transitions.medium};
  }
  
  &:hover {
    color: ${theme.colors.text.accent};
    transform: translateY(-2px);
    
    &::after {
      width: 100%;
    }
  }
  
  &:focus {
    outline: none;
    box-shadow: ${theme.shadows.focus};
  }
`;

const MotionArrow = motion(FaArrowRight);
const ArrowIcon = styled(MotionArrow)`
  margin-left: 8px;
  font-size: 1rem;
`;

// Solutions Section
const SolutionsContainer = styled.div`
  padding: 100px 0;
  background: ${theme.colors.gradient.dark};
  border-top: 1px solid ${theme.colors.secondary};
  border-bottom: 1px solid ${theme.colors.secondary};
`;

const SolutionsWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const SolutionsH2 = styled.h2`
  color: #fff;
  font-size: 2.5rem;
  margin-bottom: 64px;
  text-align: center;
  
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const SolutionsCards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 24px;
  margin-top: 48px;
  margin-bottom: 48px;
  
  @media screen and (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 20px;
  }
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const SolutionCard = styled(motion.div)`
  background: ${theme.colors.background.dark};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  border-radius: 10px;
  max-height: 400px;
  padding: 30px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease-in-out;
  border: 1px solid ${theme.colors.secondary};
  
  &:hover {
    transform: scale(1.02);
    transition: all 0.2s ease-in-out;
    cursor: pointer;
    box-shadow: 0 5px 15px rgba(212, 175, 55, 0.2);
  }
`;

const SolutionIcon = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$bg'
})`
  height: 160px;
  width: 160px;
  margin: 0 auto 20px auto;
  background: ${({ $bg }) => $bg};
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  
  @media screen and (max-width: 480px) {
    height: 130px;
    width: 130px;
  }
`;

const SolutionH3 = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 10px;
  color: ${theme.colors.secondary};
  text-align: center;
`;

const SolutionP = styled.p`
  font-size: 1rem;
  text-align: center;
  color: ${theme.colors.text.primary};
`;

const SolutionsBtn = styled(Link)`
  display: block;
  margin: 0 auto;
  text-align: center;
  border-radius: 50px;
  background: #01BF71;
  white-space: nowrap;
  padding: 12px 30px;
  color: #010606;
  font-size: 16px;
  outline: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  width: fit-content;
  
  &:hover {
    transition: all 0.2s ease-in-out;
    background: #fff;
    color: #010606;
  }
`;

// Call to Join Section
const JoinContainer = styled.div`
  padding: 80px 20px;
  background: linear-gradient(to right, #010606, #01BF71);
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media screen and (max-width: 480px) {
    padding: 60px 15px;
  }
`;

const JoinWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  text-align: center;
`;

const JoinH2 = styled.h2`
  color: #fff;
  font-size: 2.5rem;
  margin-bottom: 24px;
  
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const JoinP = styled.p`
  color: #fff;
  font-size: 1.2rem;
  max-width: 600px;
  margin: 0 auto 48px auto;
`;

const JoinBtn = styled(Link)`
  border-radius: 50px;
  background: ${theme.colors.gradient.gold};
  white-space: nowrap;
  padding: 14px 32px;
  color: #010606;
  font-size: 20px;
  outline: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  
  &:hover {
    transition: all 0.2s ease-in-out;
    background: #fff;
    color: #010606;
  }
`;

// Logo Components
const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const LogoIcon = styled.div`
  position: relative;
  font-size: 3.5rem;
  color: ${theme.colors.secondary};
  margin-bottom: 0.5rem;
  
  @media screen and (max-width: 768px) {
    font-size: 3rem;
  }
  
  @media screen and (max-width: 480px) {
    font-size: 2.5rem;
  }
`;

const Home = () => {
  return (
    <>
      <HeroContainer role="banner" aria-label="Hero section">
        <HeroContent
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <HeroH1 variants={itemVariants}>
            Building the Future with African Innovation
          </HeroH1>
          <HeroP variants={itemVariants}>
            Afrotech Group is driving transformation through tech-based solutions for communities, businesses, and government.
          </HeroP>
          <HeroBtnWrapper variants={itemVariants}>
            <HeroBtn 
              to="/solutions" 
              primary="true"
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              aria-label="Explore our solutions"
            >
              Explore Our Solutions
            </HeroBtn>
            <HeroBtn 
              to="/contact#contact-form"
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              aria-label="Contact us today"
            >
              Contact Us Today
            </HeroBtn>
          </HeroBtnWrapper>
        </HeroContent>
        <motion.div 
          style={{ 
            position: 'absolute', 
            bottom: '30px', 
            left: '50%', 
            transform: 'translateX(-50%)',
            zIndex: 2 
          }}
          animate={{ 
            opacity: 0.8 
          }}
          transition={{ 
            duration: 0.5 
          }}
        >
          <FaChevronDown color="white" size={24} aria-hidden="true" />
        </motion.div>
      </HeroContainer>
      
      <AboutPreviewContainer role="region" aria-label="About us preview">
        <AboutPreviewWrapper
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <AboutPreviewH2 variants={itemVariants}>
            Who We Are
          </AboutPreviewH2>
          <AboutPreviewP variants={itemVariants}>
            From digital platforms to community tech programs, we aim to empower Africa through innovative technologies.
            Our solutions are designed to address real challenges faced by communities, businesses, and government institutions.
          </AboutPreviewP>
          <AboutPreviewBtn 
            to="/about"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            aria-label="Learn more about Afrotech Group"
          >
            Learn More About Us <ArrowIcon animate={{ x: 0 }} whileHover={{ x: 5 }} />
          </AboutPreviewBtn>
        </AboutPreviewWrapper>
      </AboutPreviewContainer>
      
      <SolutionsContainer>
        <SolutionsWrapper>
          <SolutionsH2>Featured Solutions</SolutionsH2>
          <SolutionsCards>
            <SolutionCard
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <SolutionIcon $bg={`linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent})`}>
                <LogoContainer>
                  <LogoIcon>
                    <FaDatabase />
                    <FaSearch style={{ position: 'absolute', bottom: 0, right: 0, fontSize: '1.8rem' }} />
                  </LogoIcon>
                </LogoContainer>
              </SolutionIcon>
              <SolutionH3>DataFind</SolutionH3>
              <SolutionP>
                Asset Tracking & Recovery solution that helps law enforcement, businesses, and individuals verify ownership and recover stolen assets.
              </SolutionP>
            </SolutionCard>
            
            <SolutionCard
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <SolutionIcon $bg="linear-gradient(135deg, #f8bbd0, #e91e63)">
                <LogoContainer>
                  <LogoIcon style={{ color: '#fff' }}>
                    <FaCut />
                  </LogoIcon>
                </LogoContainer>
              </SolutionIcon>
              <SolutionH3>Beauty at Home</SolutionH3>
              <SolutionP>
                Salon Booking App that connects users with local beauticians, salons, and service providers for convenient home services.
              </SolutionP>
            </SolutionCard>
            
            <SolutionCard
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <SolutionIcon $bg="linear-gradient(135deg, #bbdefb, #2196f3)">
                <LogoContainer>
                  <LogoIcon style={{ color: '#fff' }}>
                    <FaNewspaper />
                  </LogoIcon>
                </LogoContainer>
              </SolutionIcon>
              <SolutionH3>CloseUp News</SolutionH3>
              <SolutionP>
                Journalism & Media Platform providing a space for African journalists and storytellers to publish news and engage with readers.
              </SolutionP>
            </SolutionCard>
          </SolutionsCards>
          <SolutionsBtn to="/solutions">View All Solutions</SolutionsBtn>
        </SolutionsWrapper>
      </SolutionsContainer>
      
      <JoinContainer>
        <JoinWrapper>
          <JoinH2>Partner with Afrotech Group</JoinH2>
          <JoinP>
            Join us in our mission to build lasting impact through innovative technology solutions tailored for Africa.
          </JoinP>
          <JoinBtn to="/partners">Become a Partner</JoinBtn>
        </JoinWrapper>
      </JoinContainer>
    </>
  );
};

export default Home;
